import _ from 'lodash'
import format from 'date-fns/format'
import addMonths from 'date-fns/add_months'
import startOfMonth from 'date-fns/start_of_month'
import { pgRtDayRange, pgRtMonthly } from './pgReadApi'
import { getCompanyTimezone, toCompanyDate } from './utils/companyTimezone'

export type ScopeKind =
  | { kind: 'preset'; value: 'today' | 'thisWeek' | 'thisMonth' | 'lastMonth' | 'allTime' }
  | { kind: 'month'; year: number; month: number }

const DEFAULT_SCOPE: ScopeKind = { kind: 'preset', value: 'thisMonth' }
const ALL_TIME_MONTH_WINDOW = 16

function normalizeSearchValue(value: any) {
  return value === undefined || value === null ? '' : String(value).toLowerCase()
}

function filterRunningTrackRows(rows: Array<any>, searchValue: any) {
  const searchTokens = normalizeSearchValue(searchValue).trim().split(/\s+/).filter(Boolean)
  if (!searchTokens.length) return rows
  return _.filter(rows, (item: any) => {
    const searchText = [
      item && item.name,
      item && item.email,
      item && item.displayName,
      item && item.display_name,
      item && item.nickname,
      item && item.first_name,
      item && item.last_name,
      item && item.booster_name,
    ].map(normalizeSearchValue).join(' ')
    return searchTokens.every((token) => searchText.indexOf(token) > -1)
  })
}

function sourceGetterForScope(scope: ScopeKind): string {
  if (scope.kind === 'preset') {
    switch (scope.value) {
      case 'today':     return 'happilyvillage/runningtrack_day_all'
      case 'thisWeek':  return 'happilyvillage/runningtrack_week_all'
      case 'thisMonth': return 'happilyvillage/runningtrack_month_all'
      case 'lastMonth': return 'happilyvillage/runningtrack_last_month_all'
      case 'allTime':   return 'happilyvillageRunningtrack/user_running'
    }
  }
  return 'happilyvillage/runningtrack_arbitrary_month_all'
}

function rankRows(rows: Array<any>): Array<any> {
  // Assumes input is already sorted by distance desc. Rank is positional from that.
  return rows.map((r, i) => ({ ...r, rank: i + 1 }))
}

function mergeRunnerDistance(runnersByEmail: { [email: string]: any }, row: any) {
  const email = row && row.email ? String(row.email) : ''
  if (!email) return

  const distance = Number(row.distance) || 0
  if (distance <= 0) return

  const key = email.toLowerCase()
  if (!runnersByEmail[key]) {
    runnersByEmail[key] = {
      ...row,
      email,
      name: row.name || email,
      distance: 0,
    }
  }

  runnersByEmail[key].distance += distance
  const distanceMain = row.distance_main !== undefined ? row.distance_main : row.distanceMain
  if (distanceMain !== undefined || runnersByEmail[key].distance_main !== undefined) {
    runnersByEmail[key].distance_main = (Number(runnersByEmail[key].distance_main) || 0) + (Number(distanceMain) || 0)
  }
}

function allTimeWindowMonths(dateNow: Date) {
  const months: Array<string> = []
  for (let x = 0; x < ALL_TIME_MONTH_WINDOW; x++) {
    months.push(format(addMonths(dateNow, -x), 'YYYY-MM'))
  }
  return months
}

function rowsFromRunnerMap(runnersByEmail: { [email: string]: any }) {
  return _.orderBy(Object.values(runnersByEmail), ['distance'], ['desc'])
}

function emailFromMonthlyDocId(docId: string) {
  const match = /^(.+)-\d{4}-\d{2}$/.exec(String(docId || ''))
  return match ? match[1] : ''
}

async function getPgDayRangeAllTimeRows(dateNow: Date) {
  const start = format(startOfMonth(addMonths(dateNow, -(ALL_TIME_MONTH_WINDOW - 1))), 'YYYY-MM-DD')
  const end = format(dateNow, 'YYYY-MM-DD')
  const rows = await pgRtDayRange(start, end)
  if (rows === null) return null

  const runnersByEmail: { [email: string]: any } = {}
  rows.forEach((row: any) => mergeRunnerDistance(runnersByEmail, row))
  return rowsFromRunnerMap(runnersByEmail)
}

async function getPgMonthlyAllTimeRows(dateNow: Date) {
  const months = allTimeWindowMonths(dateNow)
  const monthlyRows = await Promise.all(months.map((ym) => pgRtMonthly(ym, 10000)))
  if (monthlyRows.some((rows) => rows === null)) return null

  const runnersByEmail: { [email: string]: any } = {}
  monthlyRows.forEach((rows) => {
    const rowsForMonth = rows || []
    rowsForMonth.forEach((row: any) => mergeRunnerDistance(runnersByEmail, row))
  })

  return rowsFromRunnerMap(runnersByEmail)
}

async function getPgAllTimeRows(company: string) {
  const timezone = await getCompanyTimezone(company)
  const dateNow = toCompanyDate(new Date(), timezone)
  const dayRangeRows = await getPgDayRangeAllTimeRows(dateNow)
  if (dayRangeRows && dayRangeRows.length) return dayRangeRows
  return getPgMonthlyAllTimeRows(dateNow)
}

function getFirestoreAllTimeRows(company: string) {
  const firebase = require('firebase/app').default || require('firebase/app')
  require('firebase/firestore')
  return firebase.firestore().collection('users_running')
    .where('company', '==', company).get()
    .then((querySnapshot: any) => {
      let user_running: Array<any> = []
      querySnapshot.forEach((doc: any) => {
        const runningData = doc.data()
        if (runningData.distance > 0) {
          user_running.push({ ...runningData, email: runningData.email || doc.id })
        }
      })
      user_running = _.orderBy(user_running, ['distance'], ['desc'])
      return user_running
    })
}

function getFirestoreMonthlyAllTimeRows(company: string) {
  const firebase = require('firebase/app').default || require('firebase/app')
  require('firebase/firestore')
  return firebase.firestore().collection('running_track_month')
    .where('company', '==', company).get()
    .then((querySnapshot: any) => {
      const runnersByEmail: { [email: string]: any } = {}
      querySnapshot.forEach((doc: any) => {
        const runningData = doc.data()
        const email = runningData.email || emailFromMonthlyDocId(doc.id)
        mergeRunnerDistance(runnersByEmail, { ...runningData, email })
      })
      return rowsFromRunnerMap(runnersByEmail)
    })
}

export default {
  namespaced: true,
  state: {
    scope: DEFAULT_SCOPE,
    loading: false,
    inputSearching: '',
    sortField: 'distance',
    sortOrder: 'ascending',
    page: 1,
    sizePage: 10,
    // Keep user_running for the allTime scope. It is populated from PG when
    // available, then Firestore monthly rollups, then legacy users_running.
    user_running: [] as Array<any>,
  },
  mutations: {
    setScope(state: any, scope: ScopeKind) {
      state.scope = scope
      state.page = 1
      state.sortField = 'distance'
      state.sortOrder = 'ascending'
    },
    setLoading(state: any, loading: boolean) {
      state.loading = loading
    },
    setInputSearching(state: any, value: any) {
      state.inputSearching = value || ''
      state.page = 1
    },
    setPage(state: any, page: number) {
      state.page = page
    },
    setSort(state: any, config: { field: string; order: string }) {
      state.sortField = config.field
      state.sortOrder = config.order
    },
    setUserRunning(state: any, rows: Array<any>) {
      state.user_running = rows
    },
  },
  actions: {
    /**
     * Loads data for the active scope. Idempotent — relies on the upstream
     * happilyvillage module to cache preset data (already loaded by
     * getRunningTrackAll on app boot) and on getRunningTrackByMonth to
     * cache by month key.
     */
    loadScope({ state, dispatch, commit }: any) {
      const scope = state.scope
      if (scope.kind === 'month') {
        // FIXME: rapid month switching can land out-of-order Firestore responses
        // in the shared upstream `runningtrack_arbitrary_month_all` slot.
        // Acceptable for now (last-clicked-wins is rare in practice); revisit if
        // users complain that picking month A then quickly month B shows A's data.
        commit('setLoading', true)
        return dispatch('happilyvillage/getRunningTrackByMonth', { year: scope.year, month: scope.month }, { root: true })
          .finally(() => commit('setLoading', false))
      }
      if (scope.kind === 'preset' && scope.value === 'allTime' && !state.user_running.length) {
        commit('setLoading', true)
        return dispatch('getRunningTrackAll').finally(() => commit('setLoading', false))
      }
      // Preset windows (today / week / thisMonth / lastMonth) come from
      // happilyvillage/getDataRunningTrack which is dispatched once in the
      // component's created() hook. No additional fetch needed here.
      //
      // Known UX gap: on first paint, getDataRunningTrack is still in flight,
      // and because we don't toggle `loading` for presets, the table briefly
      // shows the empty state before rows arrive. Acceptable trade-off — the
      // proper fix is to await getDataRunningTrack from created() and toggle
      // `loading` around it, but that action wasn't promise-returning at the
      // time of this refactor.
      return Promise.resolve()
    },
    async getRunningTrackAll({ commit, rootState }: any) {
      const company = rootState.authen.user.company
      let user_running: Array<any> | null = null

      try {
        user_running = await getPgAllTimeRows(company)
      } catch (error) {
        console.error('getRunningTrackAll PG aggregation failed:', error)
      }

      if (!user_running || !user_running.length) {
        user_running = await getFirestoreMonthlyAllTimeRows(company)
      }

      if (!user_running || !user_running.length) {
        user_running = await getFirestoreAllTimeRows(company)
      }

      commit('setUserRunning', user_running)
    },
  },
  getters: {
    scope: ({ scope }: any) => scope,
    loading: ({ loading }: any) => loading,
    user_running: ({ user_running }: any) => user_running,
    /**
     * Filtered, distance-ranked rows for the active scope — no user-sort,
     * no pagination. Shared between the table (via currentScopeData) and the
     * export. Consumers that need the full dataset (e.g. exports, column
     * visibility checks) should use this instead of inspecting the paginated
     * `currentScopeData.dataTable`.
     */
    currentScopeFilteredRows: (state: any, _getters: any, _rootState: any, rootGetters: any) => {
      const sourceGetter = sourceGetterForScope(state.scope)
      const sourceRows: Array<any> = rootGetters[sourceGetter] || []
      const filtered = filterRunningTrackRows(sourceRows, state.inputSearching)
      return rankRows(_.orderBy(filtered, ['distance'], ['desc']))
    },
    /**
     * Returns { dataTable, page, results } shaped exactly like the
     * old toDayData/weekData/monthData/etc. getters — drop-in
     * replacement for the component.
     */
    currentScopeData: (state: any, getters: any) => {
      const ranked = getters.currentScopeFilteredRows
      // distance sort uses inverted direction (matches legacy getters).
      let order = state.sortOrder
      if (state.sortField === 'distance') {
        order = order === 'ascending' ? 'desc' : 'asc'
      } else {
        order = order === 'ascending' ? 'asc' : 'desc'
      }
      const sorted = _.orderBy(ranked, [state.sortField], [order as any])
      const start = (state.page - 1) * state.sizePage
      const end = start + state.sizePage
      return {
        dataTable: sorted.slice(start, end),
        page: state.page,
        results: sorted.length,
      }
    },
  },
}
